import { useCallback, useEffect, useMemo, useState } from 'react';
import { dataService } from '@/api';
import { useAuth } from './useAuth';
import { useHealth } from './useHealth';

const POLL_MS = 60_000;

export function useNotifications() {
  const { user } = useAuth();
  const { status: healthStatus, database } = useHealth();
  const [notifications, setNotifications] = useState([]);
  const isUsingDB =
    healthStatus === 'ok' && (database === 'connected' || database === 'connecting');

  const load = useCallback(async () => {
    if (!user) {
      setNotifications([]);
      return;
    }
    if (!isUsingDB) {
      setNotifications([]);
      return;
    }
    try {
      const list = await dataService.listNotifications();
      const mapped = list.map((n) => ({
        id: (n._id || n.id)?.toString?.() ?? String(n._id || n.id),
        type: n.type,
        title: n.title,
        message: n.message,
        userId: String(n.userId?._id ?? n.userId),
        relatedId: n.relatedId ? String(n.relatedId) : undefined,
        read: !!n.read,
        createdAt: new Date(n.createdAt || Date.now()).getTime(),
      }));
      setNotifications(mapped);
    } catch {
      setNotifications([]);
    }
  }, [user, isUsingDB]);

  useEffect(() => {
    void load();
    if (!user || !isUsingDB) return undefined;
    const id = setInterval(() => {
      if (document.visibilityState === 'visible') void load();
    }, POLL_MS);
    const onFocus = () => void load();
    window.addEventListener('focus', onFocus);
    return () => {
      clearInterval(id);
      window.removeEventListener('focus', onFocus);
    };
  }, [load, user, isUsingDB]);

  const markAsRead = async (id) => {
    setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
    if (!isUsingDB) return;
    await dataService.updateNotification(id, { read: true }).catch(() => {});
  };

  const markAllAsRead = async () => {
    const unread = notifications.filter((n) => !n.read);
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    if (!isUsingDB) return;
    await Promise.all(
      unread.map((n) => dataService.updateNotification(n.id, { read: true }).catch(() => {}))
    );
  };

  const deleteNotification = async (id) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
    if (!isUsingDB) return;
    await dataService.deleteNotification(id).catch(() => {});
  };

  const createNotification = async (notification) => {
    if (!isUsingDB) return;
    await dataService.createNotification(notification).catch(() => {});
    await load();
  };

  const userNotifications = useMemo(
    () =>
      user
        ? notifications
            .filter((n) => n.userId === user.id)
            .filter((n) => !(n.title === 'Password reset' && user.mustChangePassword === false))
            .sort((a, b) => b.createdAt - a.createdAt)
        : [],
    [notifications, user?.id, user?.mustChangePassword]
  );

  const unreadCount = userNotifications.filter((n) => !n.read).length;

  return {
    notifications: userNotifications,
    unreadCount,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    createNotification,
    refresh: load,
  };
}
